import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { IoChevronUp, IoChevronDown } from "react-icons/io5";
import { ModuleLayout } from "../../../layouts";
import {
  ContentCard,
  Button,
  FormField,
  Toggle,
  Checkbox,
  Loader,
} from "../../../components";
import { ConfirmModal } from "../components";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";
import {
  getPermissionsApi,
  createPermissionGroupApi,
  getPermissionGroupByIdApi,
  updatePermissionGroupApi,
} from "../../../DAL";

// ── Collapsible permission section ───────────────────────────────────────────
const PermissionSection = ({ label, permissions, checkedIds, onToggle }) => {
  const [open, setOpen] = useState(true);
  const selectedCount = permissions.filter((p) => checkedIds.has(p.id)).length;

  return (
    <div className="border-b border-[#E3E3E3] last:border-0">
      <button
        type="button"
        className="flex items-center justify-between w-full py-3 cursor-pointer"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-sm font-semibold text-[#333]">
          {label}{" "}
          <span className="text-xs font-normal text-gray-400 ml-1">
            {selectedCount}/{permissions.length}
          </span>
        </span>
        {open ? (
          <IoChevronUp className="w-4 h-4 text-primary-500" />
        ) : (
          <IoChevronDown className="w-4 h-4 text-primary-500" />
        )}
      </button>

      {open && (
        <div className="space-y-1 pb-3">
          {permissions.map((p) => (
            <div
              key={p.id}
              onClick={() => onToggle(p.id)}
              className="flex items-center gap-3 cursor-pointer select-none py-1.5"
            >
              <Checkbox
                checked={checkedIds.has(p.id)}
                onChange={() => onToggle(p.id)}
              />
              <span className="text-xs text-[#333]">{p.display_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PermissionGroupForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const isEdit = location.pathname === ROUTES_CONSTANTS.UPDATE_PERMISSION_GROUP;
  const groupId = location.state?.id;

  useEffect(() => {
    if (isEdit && !groupId) {
      navigate(ROUTES_CONSTANTS.PERMISSION_GROUPS, { replace: true });
    }
  }, [isEdit, groupId, navigate]);

  const [active, setActive] = useState(true);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [confirmData, setConfirmData] = useState(null);

  const methods = useForm({
    defaultValues: { groupName: "", description: "" },
  });
  const { reset } = methods;

  // ── Permissions from API (grouped by module) ──────────────────────────────
  const { data: permissionsData } = useQuery({
    queryKey: [QUERY_CONSTANTS.PERMISSIONS],
    queryFn: getPermissionsApi,
  });

  const permissionSections = (() => {
    const perms = permissionsData?.data ?? [];
    const map = {};
    perms.forEach((p) => {
      if (!map[p.module]) map[p.module] = [];
      map[p.module].push(p);
    });
    return Object.entries(map).map(([module, items]) => ({ module, items }));
  })();

  const togglePerm = (id) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── Fetch group for edit mode ─────────────────────────────────────────────
  const { data: groupData, isLoading: isLoadingGroup } = useQuery({
    queryKey: [QUERY_CONSTANTS.PERMISSION_GROUP, groupId],
    queryFn: async () => {
      const res = await getPermissionGroupByIdApi(groupId);
      return res.data;
    },
    enabled: isEdit && !!groupId,
  });

  useEffect(() => {
    if (!isEdit || !groupData) return;
    reset({
      groupName: groupData.display_name || groupData.name || "",
      description: groupData.description || "",
    });
    setActive(!!groupData.is_active);
    setCheckedIds(new Set((groupData.permissions ?? []).map((p) => p.id)));
  }, [isEdit, groupData, reset]);

  // ── Mutation ─────────────────────────────────────────────────────────────
  const { mutate: submitGroup, isPending } = useMutation({
    mutationFn: (data) => {
      const payload = {
        name: data.groupName,
        display_name: data.groupName,
        description: data.description,
        is_active: active,
        permissions: [...checkedIds],
      };
      return isEdit
        ? updatePermissionGroupApi(groupId, payload)
        : createPermissionGroupApi(payload);
    },
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      queryClient.invalidateQueries({
        queryKey: [QUERY_CONSTANTS.PERMISSION_GROUPS],
      });
      navigate(ROUTES_CONSTANTS.PERMISSION_GROUPS);
    },
  });

  const onSubmit = (data) =>
    setConfirmData({ ...data, selectedCount: checkedIds.size });

  const handleConfirm = () => {
    submitGroup(confirmData);
    setConfirmData(null);
  };

  if (isEdit && isLoadingGroup) return <Loader />;

  return (
    <ModuleLayout
      title={t('permissionGroups.title')}
      onBack={() => navigate(ROUTES_CONSTANTS.PERMISSION_GROUPS)}
    >
      <ContentCard className="h-full">
        <h2 className="text-2xl font-medium text-black mb-1">
          {isEdit ? t('permissionGroups.updateTitle') : t('permissionGroups.createTitle')}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {isEdit
            ? t('permissionGroups.updateSubtitle')
            : t('permissionGroups.createSubtitle')}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8">
          {/* Left — group details */}
          <div className="lg:pr-8 lg:border-r border-[#e3e3e3] pb-8 lg:pb-0 space-y-4">
            <FormProvider {...methods}>
              <form
                id="group-form"
                onSubmit={methods.handleSubmit(onSubmit)}
                noValidate
              >
                <div className="space-y-5">
                  <FormField
                    name="groupName"
                    label={t('permissionGroups.groupName')}
                    placeholder={t('permissionGroups.groupNamePlaceholder')}
                  />
                  <FormField
                    name="description"
                    type="textarea"
                    rows={4}
                    label={t('permissionGroups.groupDescription')}
                    placeholder={t('permissionGroups.groupDescriptionPlaceholder')}
                  />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-[#666]">{t('permissionGroups.activeLabel')}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {t('permissionGroups.activeDesc')}
                      </p>
                    </div>
                    <Toggle checked={active} onChange={setActive} />
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>

          {/* Right — permissions */}
          <div className="flex flex-col">
            <p className="text-xs font-medium text-[#666] mb-3">{t('permissionGroups.permissionsLabel')}</p>
            <div className="border border-[#E3E3E3] rounded-lg px-5 py-1 flex-1 overflow-y-auto">
              {permissionSections.map(({ module, items }) => (
                <PermissionSection
                  key={module}
                  label={module}
                  permissions={items}
                  checkedIds={checkedIds}
                  onToggle={togglePerm}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button
            variant="secondary"
            size="lg"
            onClick={() => navigate(ROUTES_CONSTANTS.PERMISSION_GROUPS)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="lg"
            loading={isPending}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {isEdit ? t('permissionGroups.btnUpdate') : t('permissionGroups.btnCreate')}
          </Button>
        </div>
      </ContentCard>

      {confirmData && (
        <ConfirmModal
          title={
            isEdit
              ? t('permissionGroups.confirmUpdateTitle')
              : t('permissionGroups.confirmCreateTitle')
          }
          confirmLabel={isEdit ? t('permissionGroups.confirmUpdateLabel') : t('permissionGroups.confirmCreateLabel')}
          onCancel={() => setConfirmData(null)}
          onConfirm={handleConfirm}
          loading={isPending}
        >
          <p>
            {isEdit
              ? t('permissionGroups.confirmUpdateText')
              : t('permissionGroups.confirmCreateText')}
          </p>
          <div className="space-y-1 text-primary-500 mt-2">
            <p>{t('permissionGroups.confirmGroupName')}: [{confirmData.groupName}]</p>
            <p>{t('permissionGroups.confirmStatus')}: [{active ? t('common.active') : t('common.inactive')}]</p>
            <p>
              {t('permissionGroups.confirmPermissions')}: [{confirmData.selectedCount} {t('permissionGroups.confirmPermissionsCount')}]
            </p>
          </div>
          <p className="mt-3">
            {t('permissionGroups.confirmWarning')}
          </p>
        </ConfirmModal>
      )}
    </ModuleLayout>
  );
};

export default PermissionGroupForm;
